import { IntlMessageFormat } from "intl-messageformat";

// const isoByteUnits: string[] = ["Byte", "KiB", "MiB", "GiB", "TiB", "PiB"];
const ourByteUnits: string[] = ["Byte", "kB", "MB", "GB", "TB", "PB"];
const KiB = 1024;

/**
 * Return a file size and a unit for the size.
 *   The returned size is divided by baseFactor to the largest unit with at least one leading non-zero digit.
 *   Per default it scales the unit to multiples of 1024 (Byte, kB, MB, ...).
 * @param  sizeInBytes  the number of bytes in the file
 * // @param  baseFactor   the multiplication factor when going from one unit to the next
 * // @param  unitSeries   list of units for the base factor and the measured value
 * @return {size: sizeInUnit, unit: closest SI unit as string, factor: multiplication factor from unit to bytes}
 *  Adapts unit for small and big files, making sure that small files are not displayed with size "0" in range < 1kB
 */
export function humanReadableFileSize(sizeInBytes = 0, showBytePrecision = false): { size: string; unit: string } {
  const baseFactor = KiB;
  const unitSeries = ourByteUnits;
  let pos = 0;
  let factor = 1;
  const size = Math.max(0, sizeInBytes);

  const pluralUnit = new IntlMessageFormat(
    `{size, plural,
        =1 {{unit}}
        other {{unit}s}
      }`,
    "en-US",
  );

  if (size === 0) {
    return {
      size: "0",
      unit: showBytePrecision ? (pluralUnit.format<string>({ size: 0, unit: unitSeries[0] }) as string) : unitSeries[1],
    };
  }

  while (sizeInBytes > factor * baseFactor - 1 && pos < unitSeries.length - 1) {
    factor *= baseFactor;
    pos += 1;
  }

  // we never want to see the size in Bytes, we rather want <1 KB
  if (!showBytePrecision && pos === 0) {
    return { size: "<1", unit: unitSeries[1] };
  }

  const sizeInString = (size / factor).toFixed(0);
  const unit =
    pos > 0
      ? unitSeries[pos]
      : (pluralUnit.format<string>({ size: Number(sizeInString), unit: unitSeries[pos] }) as string);

  return { size: sizeInString, unit };
}
